import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Container,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Header } from "../header/header";

const ContactPage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // React Hook Form Setup
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange", // Validates on change
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  // Handle Form Submission
  const onSubmit = async (data: unknown) => {
    try {
      await fetch(
        `${import.meta.env.VITE_API_URL}/api/send-support-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

    } catch (error) {
      console.error("Error submitting form:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred. If the error persists, please contact us."
      );
    } finally {
      setSubmitted(true);
    }
  };

  return (
    <>
      <Header />
      <Container
        maxWidth="md"
        className="pb-12 flex flex-col gap-6 items-start"
      >
        {/* Page Title */}
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Contact Us
        </Typography>

        {/* Contact Form */}
        <Card className="w-full">
          <CardContent>
            <Typography variant="body1" color="textSecondary" paragraph>
              Fill out the form below, and we’ll get back to you as soon as
              possible.
            </Typography>

            {submitted ? (error ? (<Alert severity="error">
              {error ?? 'An unexpected error occurred. If the error persists, please email us directly.'}
            </Alert>) : (
              <Alert severity="success">
                Thank you! Your message has been sent.
              </Alert>)
            ) : (
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
              >
                {/* Name Field */}
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: "Full name is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Full Name"
                      variant="outlined"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />

                {/* Email Field */}
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: "Email is required",
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Email"
                      variant="outlined"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />

                {/* Phone Field */}
                <Controller
                  name="phone"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^[0-9+\-()\s]*$/,
                      message: "Invalid phone number format",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Phone Number (optional)"
                      variant="outlined"
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />

                {/* Message Field */}
                <Controller
                  name="message"
                  control={control}
                  rules={{ required: "Message is required" }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Message"
                      variant="outlined"
                      multiline
                      rows={4}
                      error={!!errors.message}
                      helperText={errors.message?.message}
                    />
                  )}
                />

                {/* Submit Button (Disabled Until Valid) */}
                <Button
                  aria-label="submit"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid}
                >
                  Submit
                </Button>
                <Divider />
                <p>
                  If the above form doesn't work, please contact us at{" "}
                  <a
                    className="text-blue-500"
                    href="mailto:calum@diamedic.co.uk"
                  >
                    calum@diamedic.co.uk
                  </a>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
        <div
          className="my-8 flex flex-col items-start text-start"
        >
          <h3 className="text-xl md:text-2xl font-semibold mb-2 text-black">
            Want to chat with us directly?
          </h3>
          <p className="text-md md:text-lg mb-4 text-gray-700 max-w-xl">
            You can still be part of the community! Join our Discord server to chat with others, ask questions, and follow Diamedic's progress.
          </p>
          <Button
            href="https://discord.gg/qV3BTjKMv3"
            target="_blank"
            size="large"
            rel="noopener noreferrer"
            sx={{
              background: "#5865F2",
              color: "#fff",
              "&:hover": {
                background: "#4752C4",
                transform: "scale(1.05)",
              },
            }}
            variant="contained"
            className="rounded-full px-6 py-3 font-semibold shadow-md"
          >

            Join Our Discord
          </Button>
        </div>

        {/* Home Button */}
        <Button
          aria-label="home"
          variant="contained"
          className="md:w-fit w-full mt-6"
          onClick={() => navigate("/")}
        >
          Home
        </Button>
      </Container>
    </>
  );
};

export default ContactPage;
